import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import type { ChangelogVersion } from "@nodus/ui";
import type { PortalCapacityResponse } from "@nodus/capacity";
import { useAuthStore } from "@/lib/auth";
import { getConfig } from "@/lib/config";
import { rememberedSiteId } from "@/lib/vanity";
import { maintenanceFromError, setMaintenance } from "@/lib/maintenance";
import type { FeedContents, IssueTenantCalendarFeedBody } from "@/lib/calendar-feeds";
import type { RoomPricing } from "@/lib/meeting-room-pricing";

const API_BASE_URL = getConfig().API_BASE_URL!;

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem("access_token");
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const siteId = getConfig().SITE_ID;
  if (siteId && config.headers) {
    config.headers["X-Site-ID"] = siteId;
  }
  return config;
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (token) {
      resolve(token);
    } else {
      reject(error);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Maintenance mode (#459): App swaps in the maintenance screen.
    const maintenance = maintenanceFromError(error);
    if (maintenance) {
      setMaintenance(maintenance);
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          return api(originalRequest);
        });
      }

      isRefreshing = true;

      const refreshToken = localStorage.getItem("refresh_token");
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/tenant-portal/auth/refresh/`, {
            refresh: refreshToken,
          });

          const { access, refresh } = response.data;
          localStorage.setItem("access_token", access);
          localStorage.setItem("refresh_token", refresh);

          processQueue(null, access);

          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${access}`;
          }
          return api(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          console.error("Token refresh failed:", refreshError);
          // Only log out if the refresh token is explicitly rejected (401/400).
          // No response (network error, 503, restart) — keep the session alive.
          const refreshStatus = (refreshError as AxiosError)?.response?.status;
          if (refreshStatus === 401 || refreshStatus === 400) {
            useAuthStore.getState().logout();
            window.location.href = "/login?reason=session_expired";
          }
        } finally {
          isRefreshing = false;
        }
      } else {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// API types
export interface LoginRequest {
  email: string;
  password: string;
}

export interface TokenResponse {
  access: string;
  refresh: string;
  tenant_id: number;
  site_id: number;
  is_tenant_admin: boolean;
  is_site_admin: boolean;
}

export interface PlatformLink {
  platform: string;
  platform_id: string;
  linked_at: string;
}

export interface OAuthProvider {
  provider: string;
  label: string;
  icon: string;
}

export interface PlatformLinkDetail {
  platform: string;
  platform_id: string;
  user_name: string;
  email: string;
  display_name: string;
  avatar_url: string;
  workspace_name: string;
}

export interface CurrentUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  tenant_id: number;
  tenant_name: string;
  site_id: number;
  site_name: string;
  tenant_role: string | null;
  is_tenant_admin: boolean;
  /** Admin on the site's customer — grants the site-scoped surfaces
   *  (e.g. site notification targets). Reported by `/tenant-portal/me/` and
   *  carried in the login/refresh payloads alongside `is_tenant_admin`. */
  is_site_admin: boolean;
  /** Nodus staff/superuser browsing the portal — unlocks every beta/feature-
   *  gated menu. Display-only. */
  is_staff?: boolean;
  platform_links?: PlatformLink[];
  hidden_from: string[];
  is_beta_tester: boolean;
  /** The member's operator has printing (PaperCut) set up. */
  print_available?: boolean;
  /** A tenant admin whose company has invoices to show — gates the Invoices menu. */
  invoices_available?: boolean;
}

export interface SlackChannel {
  id: string;
  name: string;
  is_member: boolean;
}

export interface NetworkInfo {
  name: string | null;
  vlan_id: number | null;
  prefix: string | null;
  netmask: number | null;
  speed_limit_download: number | null;
  speed_limit_upload: number | null;
}

/** One rung of the plan ladder (#313). `price_sek` is always a number here —
 *  the backend excludes unpriced tiers (NULL price = not available), and `0`
 *  means genuinely included in the rent, which renders as "Included", never
 *  "0 kr". Null and zero are different answers. */
export interface NetworkPlanTier {
  id: number;
  download_mbps: number;
  upload_mbps: number;
  price_sek: number;
}

export interface NetworkPlan {
  /** Every priced tier for the member's network, ordered slowest-first. */
  tiers: NetworkPlanTier[];
  /** null when the network has no speed limit set. */
  current_tier_id: number | null;
  /** Tenant admin on a single-tenant network — the switch buttons render.
   *  The server enforces this on PUT too; the hidden button is a consequence
   *  of the 403/409, not a substitute for it. */
  can_change: boolean;
  billing_details_complete: boolean;
  /** ISO date (YYYY-MM-DD) a downgrade is next allowed; null = not inside
   *  the 7-day cooldown. Upgrades are never throttled. */
  downgrade_available_on: string | null;
  /** The speeds actually on the network now — set even when the current tier
   *  is not on the ladder (e.g. an unpriced legacy tier). */
  current_download_mbps: number | null;
  current_upload_mbps: number | null;
}

/** Body of a refusal from PUT /network/plan/ — a `detail` string only.
 *  The billing gate uses the EXACT token "billing_details_required" so it
 *  can be keyed on; the other gates (shared network, downgrade cooldown)
 *  send human prose to show as-is. */
export interface NetworkPlanError {
  detail?: string;
}

/** Invoice-delivery details for the tenant (#313) — required before the first
 *  self-service plan change. `organisation_number` mirrors the value on the
 *  tenant record (one source of truth): non-null means staff already set it
 *  and the form shows it read-only; null means the form collects it and the
 *  backend verifies + writes it back to the tenant. */
export interface TenantBillingDetails {
  method: "email" | "einvoice";
  invoice_email: string;
  einvoice_address: string;
  reference: string;
  address_line1: string;
  address_line2: string;
  postcode: string;
  city: string;
  country: string;
  is_complete: boolean;
  organisation_number: string | null;
  /** Copy address for emailed invoices (email mode only; the accounting system clears it for e-invoice). */
  invoice_email_cc: string;
  /** Legal name on invoices and in Fortnox; "" = the tenant's name. */
  invoice_name: string;
  /** "einvoice_manual_setup": the saved id is set up by hand in the accounting system. */
  einvoice_note: "einvoice_manual_setup" | null;
  /** Peppol Directory soft-confirm of a saved PEPPOL id; null = not a PEPPOL id, or not checked. */
  peppol_registered: boolean | null;
}

export interface UpdateTenantBillingDetails {
  method: "email" | "einvoice";
  invoice_email?: string;
  einvoice_address?: string;
  reference: string;
  address_line1: string;
  address_line2?: string;
  postcode: string;
  city: string;
  /** Omitted = cleared (the PUT replaces the row) — always send the current value. */
  invoice_email_cc?: string;
  /** Omitted = kept. */
  invoice_name?: string;
}

export interface PeppolMatch {
  id: string;
  name: string;
  country: string;
  can_receive_invoice: boolean;
}

/** `skipped` = the Peppol Directory was rate-limited or unavailable; try again shortly. */
export interface PeppolLookup {
  skipped: boolean;
  matches: PeppolMatch[];
}

/** Billing PUT errors are plain `detail` prose. */
export interface BillingDetailsError {
  detail?: string;
}

/** 409 body from the org-number verify endpoint — structured so the form can
 *  name what the register returned without parsing prose. 404 (unknown
 *  number) and 502 (register unreachable) are plain `detail` errors. */
export interface OrgNumberVerifyError {
  code?: "org_number_mismatch" | "org_number_already_set" | "org_number_in_use";
  detail?: string;
  registered_name?: string | null;
}

/** 200 from the verify endpoint — the number is now on the tenant record. */
export interface OrgNumberVerifyResult {
  organisation_number: string;
  company_name: string;
}

export interface NetworkStatus {
  status: "own_network" | "wrong_network" | "external" | "detecting";
  internal_ip: string | null;
  connected_network: NetworkInfo | null;
  tenant_network: NetworkInfo | null;
  instructions: string[];
}

export interface PaginatedResponse<T> {
  results: T[];
  count: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface PrintJob {
  id: number;
  document_name: string;
  printer_name: string;
  pages: number | null;
  copies: number | null;
  is_color: boolean;
  is_double_sided: boolean;
  paper_size: string;
  cost: number | null;
  status: string;
  job_time: string;
}

export interface PrintSummary {
  total_pages: number;
  total_cost: number;
  color_pages: number;
  bw_pages: number;
  print_jobs_count: number;
  period_start: string;
  period_end: string;
  /** Site-level switch (``print_prices_enabled``). When false the backend
   *  zeroes ``total_cost`` and nulls every job ``cost`` — the portal must
   *  hide cost UI entirely rather than render zeroes. */
  show_prices: boolean;
  /** Currency symbol to render alongside costs, e.g. ``kr`` or ``$``. */
  currency_symbol: string;
  /** True when the symbol goes after the amount (``0.80 kr``). */
  currency_symbol_after: boolean;
  /** True when the member already has a live PaperCut account — the
   *  onboarding button reads "Resend instructions" instead of "Set up". */
  has_print_account: boolean;
}

export interface PrintQuota {
  quota_amount: number | null;
  used_amount: number | null;
  remaining_amount: number | null;
  period: string;
}

/** Status of the member's own 802.1X credential (#300). Never carries the
 *  secret — that exists only in the create/rotate response. */
export interface Dot1xStatus {
  /** False when the building has no 802.1X network; the portal hides the card. */
  configured: boolean;
  ssid: string | null;
  /** Certificate name the device validates. Android labels this "Domain". */
  server_name: string;
  /** The member's own sign-in identity (their email). */
  identity: string;
  has_credential: boolean;
  is_active: boolean;
  created_at: string | null;
  rotated_at: string | null;
  revoked_at: string | null;
  last_used_at: string | null;
  last_used_nas: string;
}

export interface Dot1xCredential {
  secret: string;
  username: string;
  ssid: string | null;
}

export interface WirelessInfo {
  ssid: string | null;
  password: string | null;
  pending_password: string | null;
  guest_ssid: string | null;
  guest_password: string | null;
}

export interface DoorbellScheduleDay {
  day: string;
  is_open: boolean;
  open_time: string | null;
  close_time: string | null;
}

export interface DoorbellSchedule {
  schedule: DoorbellScheduleDay[];
}

/** Wire-shape for ``GET /tenant-portal/weekly-schedule/`` — multi-slot
 *  rows + tri-state opening override. Mirrors the new tenant-portal
 *  contract added in #87 phase 1. */
export interface WeeklyScheduleSlot {
  day_of_week: number;
  start: string;
  end: string;
  available: boolean;
}

export interface WeeklySchedulePayload {
  schedules: WeeklyScheduleSlot[];
  opening_override: boolean | null;
  opening_override_until: string | null;
}

export interface FAQCategory {
  id: number;
  name: string;
  icon: string | null;
  articles: FAQArticle[];
}

export interface FAQArticle {
  id: number;
  title: string;
  content: string;
  attachments: FAQAttachment[];
}

export interface FAQAttachment {
  id: number;
  filename: string;
  description: string | null;
  file_size: number | null;
  url: string;
}

export interface TenantUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  tenant_role: string | null;
  is_tenant_admin: boolean;
}

export interface DiagnosticSessionResponse {
  ref: string;
  speedtest_url: string;
  status: string;
  created_at: string;
}

export interface SpeedtestHistoryItem {
  ref: string;
  created_at: string;
  status: string;
  download_mbps: number | null;
  upload_mbps: number | null;
  ping_ms: number | null;
  jitter_ms: number | null;
  created_by_name: string | null;
  speedtest_url: string;
}

export interface DhcpLease {
  ip_address: string;
  mac_address: string | null;
  hostname: string | null;
  status: string | null;
  last_seen: string | null;
  expires_at: string | null;
  is_static: boolean;
  static_lease_id: number | null;
  static_comment: string;
}

/** Which enforcement layers a device action reached (#310). A `partial`
 *  means the RADIUS gate is live but the controller push failed — the UI must
 *  say so rather than claim "Blocked". */
export interface DeviceActionResult {
  status: "blocked" | "unblocked" | "disconnected" | "partial" | "failed";
  policy: "applied" | "cleared" | "absent" | null;
  controller: "applied" | "failed" | "skipped";
  kicked: boolean;
  detail: string | null;
}

export interface DevicePolicy {
  id: number;
  mac_address: string;
  label: string;
  enabled: boolean;
  created_at: string;
  created_by_name: string | null;
  last_seen_at: string | null;
}

/** How a device is attached right now — one shape for both media (#310).
 *  Wireless rows carry ssid/band and either `signal` (dBm) or, where the
 *  provider only rates the link, `signal_rating` — never both meaningfully.
 *  Wired rows carry switch/port/speed. */
export interface DeviceConnection {
  /** The backend calls this `type` (PortalDeviceConnectionSchema). Do not
   *  rename it to `connection_type` to match the other endpoints — this one
   *  sends `type`, and a mismatch here does not fail, it just renders blank. */
  type: "wireless" | "wired" | null;
  ssid: string | null;
  band: string | null;
  /** Signal strength in dBm (negative). */
  signal: number | null;
  /** Provider-supplied quality rating where no dBm exists (Aruba: 1–5).
   *  Render as a word, never as a number. */
  /** A WORD, not a number — Aruba sends "good"/"fair". The portal schema
   *  declares `signal_rating: str | None`, so absent (a provider that measures
   *  dBm instead) arrives as null, not "". */
  signal_rating: string | null;
  switch_name: string | null;
  port_name: string | null;
  /** Link speed in Mbps. */
  speed: number | null;
}

/** One row of the portal devices table (#310) — a row is a MAC. */
export interface NetworkDevice {
  mac_address: string;
  hostname: string | null;
  vendor: string | null;
  ip_address: string | null;
  ip_address_type: "dhcp" | "static" | null;
  status: "online" | "offline" | "never_seen";
  last_seen: string | null;
  /** Static-lease sync state, or null for a plain dynamic address. */
  lease_state: "static" | "making_static" | "removing" | null;
  static_lease_id: number | null;
  static_ip_address: string | null;
  is_mine: boolean;
  /** Which member's 802.1X identity this device authenticated as — admin
   *  scope only; null for members and for devices that never did. */
  /** A display STRING (full name, else email) — not an object. The backend
   *  declares `signed_in_as: str | None`. */
  signed_in_as: string | null;
  blocked: boolean;
  connection: DeviceConnection | null;
}

export interface BrandingInfo {
  site_id: number | null;
  tenant_id: number | null;
  tenant_name: string | null;
  site_name: string | null;
  logo_url: string | null;
  logo_base64: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  color_scheme: string | null;
}

// API functions
export const authApi = {
  login: (data: LoginRequest) =>
    api.post<TokenResponse>("/tenant-portal/auth/login/", data),

  refresh: (refresh: string) =>
    api.post<TokenResponse>("/tenant-portal/auth/refresh/", { refresh }),
};

export const tenantApi = {
  getMe: () => api.get<CurrentUser>("/tenant-portal/me/"),

  updateMe: (data: { first_name?: string; last_name?: string; hidden_from?: string[]; is_beta_tester?: boolean }) =>
    api.patch<{ first_name: string; last_name: string; hidden_from: string[]; is_beta_tester: boolean }>("/tenant-portal/me/", data),

  getDoorPinStatus: () =>
    api.get<{ available: boolean; enabled: boolean; length: number }>("/tenant-portal/me/door-pin/"),

  setDoorPin: (pin: string) =>
    api.post<{ success: boolean }>("/tenant-portal/me/door-pin/", { pin }),

  // 802.1X personal Wi-Fi credential (#300). Lives under /dot1x, not
  // /tenant-portal: 802.1X is network admission and the same credential will
  // authenticate wired switch ports, so it is not a portal-only concept.
  getDot1xStatus: () => api.get<Dot1xStatus>("/dot1x/me/"),

  /** THE only response that ever contains the secret — show it once, then it is
   *  unrecoverable. Rotates when a credential already exists. */
  /** `secret` lets the member choose their own; omitted, the server generates one. */
  createDot1xCredential: (secret?: string) =>
    api.post<Dot1xCredential>("/dot1x/me/credential/", secret ? { secret } : {}),

  revokeDot1xCredential: () => api.delete<{ success: boolean }>("/dot1x/me/credential/"),

  getAccounts: (page = 1, pageSize = 50) =>
    api.get<PaginatedResponse<TenantUser>>("/tenant-portal/accounts/", {
      params: { page, page_size: pageSize },
    }),

  getNetwork: () => api.get<NetworkInfo>("/tenant-portal/network/"),

  // Connection-capacity for the member's OWN network — backend resolves it from
  // the authenticated tenant, no id is sent. Reuses the shared @nodus/capacity
  // response shape and components.
  getNetworkCapacity: () => api.get<PortalCapacityResponse>("/tenant-portal/network/capacity/"),

  /** The plan ladder for the member's own network — prices to EVERYONE,
   *  switching gated by `can_change` (tenant admins on single-tenant
   *  networks). */
  getNetworkPlan: () => api.get<NetworkPlan>("/tenant-portal/network/plan/"),

  /** 403 not a tenant admin; 409 with a `detail` when a gate refuses —
   *  see NetworkPlanError. */
  updateNetworkPlan: (tierId: number) =>
    api.put<NetworkPlan>("/tenant-portal/network/plan/", { tier_id: tierId }),

  getBillingDetails: () =>
    api.get<TenantBillingDetails>("/tenant-portal/tenant/billing-details/"),

  updateBillingDetails: (data: UpdateTenantBillingDetails) =>
    api.put<TenantBillingDetails>("/tenant-portal/tenant/billing-details/", data),

  /** Suggest the tenant's PEPPOL id from the Peppol Directory by its organisation number (advisory). */
  peppolLookup: () =>
    api.get<PeppolLookup>("/tenant-portal/tenant/billing-details/peppol-lookup/"),

  /** Verify a typed org number against the company register and, ONLY on a
   *  name match, write it to the tenant record. Mismatch / already set /
   *  in use refuse with a structured 409 (OrgNumberVerifyError); unknown
   *  number and register outage are 404/502. Nothing is ever saved on a
   *  refusal — a separate call from the billing PUT on purpose: the number
   *  is verified state on the tenant, not another form field. */
  verifyOrganisationNumber: (organisationNumber: string) =>
    api.post<OrgNumberVerifyResult>("/tenant-portal/tenant/organisation-number/verify/", {
      organisation_number: organisationNumber,
    }),

  identifyNetwork: (token?: string) =>
    api.post<NetworkStatus>("/tenant-portal/network/identify/", { token }),

  getPrintHistory: (page = 1, pageSize = 50, startDate?: string, endDate?: string) =>
    api.get<PaginatedResponse<PrintJob>>("/tenant-portal/print/history/", {
      params: { page, page_size: pageSize, start_date: startDate, end_date: endDate },
    }),

  getPrintSummary: (days = 30) =>
    api.get<PrintSummary>("/tenant-portal/print/summary/", {
      params: { days },
    }),

  getPrintQuota: () => api.get<PrintQuota>("/tenant-portal/print/quota/"),

  startPrinting: () =>
    api.post<{ outcome: "resent" | "invited"; message: string }>(
      "/tenant-portal/print/start/",
      {},
    ),

  getWireless: () => api.get<WirelessInfo>("/tenant-portal/wireless/"),

  changeWirelessPassword: (password: string) =>
    api.put<WirelessInfo>("/tenant-portal/wireless/password/", { password }),

  getDoorbellSchedule: () => api.get<DoorbellSchedule>("/tenant-portal/doorbell/schedule/"),

  updateDoorbellSchedule: (schedule: DoorbellScheduleDay[]) =>
    api.put<DoorbellSchedule>("/tenant-portal/doorbell/schedule/", { schedule }),

  /** New multi-slot opening-hours endpoint (#87). Replaces
   *  ``getDoorbellSchedule`` / ``updateDoorbellSchedule`` everywhere
   *  in the portal. The legacy methods above stay until the cleanup
   *  MR lands so stale tabs don't 404. */
  getWeeklySchedule: () =>
    api.get<WeeklySchedulePayload>("/tenant-portal/weekly-schedule/"),

  updateWeeklySchedule: (payload: {
    schedules: WeeklyScheduleSlot[];
    opening_override: boolean | null;
  }) => api.put<WeeklySchedulePayload>("/tenant-portal/weekly-schedule/", payload),

  listSlackChannels: () =>
    api.get<SlackChannel[]>("/tenant-portal/slack/channels/"),

  unlinkSlackBot: () =>
    api.delete("/tenant-portal/slack/bot/"),

  getFAQ: () => api.get<FAQCategory[]>("/tenant-portal/faq/"),

  createDiagnosticSession: (ipToken?: string) =>
    api.post<DiagnosticSessionResponse>("/tenant-portal/diagnostic/sessions/", {
      ip_token: ipToken || null,
    }),

  listDiagnosticSessions: () =>
    api.get<SpeedtestHistoryItem[]>("/tenant-portal/diagnostic/sessions/"),

  getNetworkLeases: () => api.get<DhcpLease[]>("/tenant-portal/network/leases/"),

  createStaticLease: (data: { ip_address: string; mac_address: string; hostname?: string; comment?: string }) =>
    api.post("/tenant-portal/network/leases/static", data),

  updateStaticLease: (id: number, data: { ip_address?: string; mac_address?: string; hostname?: string; comment?: string }) =>
    api.patch(`/tenant-portal/network/leases/static/${id}`, data),

  deleteStaticLease: (id: number) => api.delete(`/tenant-portal/network/leases/static/${id}`),

  /** The one devices table (#310) — rows are MACs. `scope=everyone` is
   *  tenant-admin only and the SERVER refuses it (403) for members; the UI
   *  hiding the chip is a consequence of that refusal, not the guard.
   *  `show` is a comma-separated set of toggles
   *  (online,offline,static,blocked,history). */
  listNetworkDevices: (params: { scope?: "mine" | "everyone"; show?: string; q?: string; page?: number }) =>
    api.get<PaginatedResponse<NetworkDevice>>("/tenant-portal/network/devices/", { params }),

  // Device actions (#310). Admins for anything on the tenant, members for
  // devices that authenticated as them — the backend enforces this; the UI
  // hiding a button is a consequence, not the guard.
  disconnectDevice: (mac: string) =>
    api.post<DeviceActionResult>("/tenant-portal/network/devices/disconnect/", { mac_address: mac }),

  blockDevice: (mac: string, label?: string) =>
    api.post<DeviceActionResult>("/tenant-portal/network/devices/block/", { mac_address: mac, label: label ?? "" }),

  unblockDevice: (mac: string) =>
    api.post<DeviceActionResult>("/tenant-portal/network/devices/unblock/", { mac_address: mac }),

  listDevicePolicies: () =>
    api.get<DevicePolicy[]>("/tenant-portal/network/devices/policies/"),

  supportChat: (message: string, conversationId?: number, context?: string) =>
    api.post<SupportChatResponse>("/support/chat/", {
      message,
      conversation_id: conversationId ?? null,
      context: context ?? null,
    }),

  listSupportConversations: () =>
    api.get<SupportConversationSummary[]>("/support/conversations/"),

  getSupportConversation: (id: number) =>
    api.get<SupportConversationDetail>(`/support/conversations/${id}/`),

  patchSupportConversation: (id: number, status: "open" | "archived" | "closed") =>
    api.patch<SupportConversationDetail>(`/support/conversations/${id}/`, { status }),

  /** Mark one thread read up to now. Called when the member opens it. */
  markSupportConversationRead: (id: number) =>
    api.post<SupportConversationSummary>(`/support/conversations/${id}/read/`),

  /** Clear every unread message across all the member's threads. */
  markAllSupportConversationsRead: () =>
    api.post<{ unread_total: number }>("/support/conversations/read-all/"),

  // Calendar feeds — tenant-facing meeting-room iCal subscription URLs.
  listCalendarFeeds: () =>
    api.get<TenantCalendarToken[]>("/tenant-portal/calendar-feeds/"),
  issueCalendarFeed: (body: IssueTenantCalendarFeedBody) =>
    api.post<IssueTenantCalendarFeedsResponse>("/tenant-portal/calendar-feeds/", body),
  revokeCalendarFeed: (id: number) =>
    api.delete(`/tenant-portal/calendar-feeds/${id}/`),
  listCalendarFeedAccesses: (id: number) =>
    api.get<TenantCalendarTokenAccess[]>(`/tenant-portal/calendar-feeds/${id}/accesses/`),
};

export interface SupportMessageResponse {
  id: number;
  role: string;
  /** Source of truth for "the AI wrote this". */
  is_ai: boolean;
  /** Attribution: "AI" when is_ai; else the human author's name; else "Anonymous". */
  author_name: string;
  /** Human author's user id (null for AI / removed authors) — distinguishes
   *  "my own message" from a colleague's on a multi-participant ticket. */
  author_user_id: number | null;
  content: string;
  created_at: string;
}

export interface SupportChatResponse {
  conversation_id: number;
  reply: string;
  history: SupportMessageResponse[];
}

export interface SupportConversationSummary {
  id: number;
  created_at: string;
  status: string;
  category: string;
  is_ticket: boolean;
  escalated_at: string | null;
  preview: string;
  message_count: number;
  /** Messages in this thread the signed-in member has not read yet. */
  unread_count: number;
}

export interface SupportConversationDetail {
  id: number;
  created_at: string;
  status: string;
  category: string;
  is_ticket: boolean;
  escalated_at: string | null;
  group_ids: number[];
  /** Conversation participants (owner-first); matches the backend payload. */
  participants: { id: number; name: string; role: string }[];
  history: SupportMessageResponse[];
}

export const brandingApi = {
  getBranding: (ipToken?: string) => {
    // The site a ?site= link named (lib/vanity.ts), for this request only — the on-site copy's own SITE_ID is
    // already sent on every request by the interceptor above.
    const remembered = getConfig().SITE_ID ? undefined : rememberedSiteId();
    return api.get<BrandingInfo>("/tenant-portal/branding/", {
      params: ipToken ? { ip_token: ipToken } : undefined,
      headers: remembered ? { "X-Site-ID": remembered } : undefined,
    });
  },
};

export interface VersionInfo {
  backend_version: string;
  /** Maintenance mode (#459); absent on older backends. */
  maintenance?: { enabled: boolean; message: string; until: string | null } | null;
}

export const coreApi = {
  getVersion: (signal?: AbortSignal) =>
    api.get<VersionInfo>("/core/version/", { signal }),
};

// Release notes (portal-surface entries only); rendered by @nodus/ui's
// ChangelogList on /changelog. The version text on the account page links there.
export const changelogApi = {
  get: (signal?: AbortSignal) =>
    api.get<{ versions: ChangelogVersion[] }>("/tenant-portal/changelog/", { signal }),
};

export interface MeetingRoomEvent {
  id: number;
  title: string;
  start: string;
  end: string;
  all_day: boolean;
  organizer_email: string;
  organizer_tenant_name: string | null;
  description: string;
  is_my_booking: boolean;
}

export interface MeetingRoom {
  id: number;
  name: string;
  description: string | null;
  floor_id: number;
  floor_name: string;
  address_id: number;
  address_name: string;
  seats: number | null;
  has_camera: boolean;
  has_tv: boolean;
  has_speaker: boolean;
  gcal_email: string | null;
  is_bookable: boolean;
  booking_slots: { start: string; end: string; available: boolean }[] | null;
  is_available: boolean;
  has_floorplan: boolean;
  floorplan_id: number | null;
  current_event: MeetingRoomEvent | null;
  next_free_slot_start: string | null;
  next_free_slot_duration_minutes: number | null;
  pricing?: RoomPricing | null;
}

export interface MeetingRoomSchedule {
  id: number;
  name: string;
  description: string | null;
  floor_name: string;
  address_name: string;
  seats: number | null;
  has_camera: boolean;
  has_tv: boolean;
  has_speaker: boolean;
  is_connected: boolean;
  is_bookable: boolean;
  events: MeetingRoomEvent[];
  free_slots: FreeSlot[];
  pricing?: RoomPricing | null;
}

export interface FreeSlot {
  start: string;
  end: string;
  duration_minutes: number;
}

export interface MeetingRoomDetail {
  id: number;
  name: string;
  description: string | null;
  floor_name: string;
  address_name: string;
  seats: number | null;
  has_camera: boolean;
  has_tv: boolean;
  has_speaker: boolean;
  gcal_email: string | null;
  is_bookable: boolean;
  has_floorplan: boolean;
  floorplan_id: number | null;
  events: MeetingRoomEvent[];
  free_slots: FreeSlot[];
  pricing?: RoomPricing | null;
}

export interface BookRoomRequest {
  title: string;
  start: string;
  end: string;
  description?: string;
}

export interface BookRoomResponse {
  event_id: number;
  gcal_event_id: string | null;
  title: string;
  start: string;
  end: string;
  /** #401: set when the booking waited for (or was let through by) the company's approval rule. */
  approval?: { state: string; expires_at: string | null; estimate_sek: number | null } | null;
  /** The room's site, IANA name: the outcome toast's times are in this zone, not the browser's. */
  site_time_zone: string;
}

export interface MyBooking {
  id: number;
  title: string;
  description: string;
  organizer_email: string;
  organizer_name: string | null;
  start: string;
  end: string;
  room_id: number;
  room_name: string;
  floor_name: string;
  address_name: string;
  /** The room's site, IANA name (e.g. "Europe/Stockholm"): booking times show in this zone, not the browser's. */
  site_time_zone?: string;
  booked_by_me: boolean;
  pricing?: RoomPricing | null;
  /** What the booking is charged (its active meeting-room charges, SEK excl. VAT); null when none. */
  charge_sek?: number | null;
  /** #401: "" | "within_allowance" | "pending" | "approved". */
  approval_state?: string;
  approval_expires_at?: string | null;
  /** The viewer is a company admin and the booking waits for a decision. */
  can_approve?: boolean;
}

export const oauthApi = {
  getProviders: () => api.get<OAuthProvider[]>("/tenant-portal/oauth/providers/"),
  exchange: (code: string) => api.post<TokenResponse>("/tenant-portal/oauth/exchange", { code }),
};

export interface WalkSegment {
  type: "walk";
  floorplan_id: number;
  floorplan_name: string;
  path: [number, number][];
  distance_meters: number;
  start_label: string;
  end_label: string;
  svg_content: string;
}

export interface TransitionSegment {
  type: "transition";
  transition_type: string;
  name: string;
  from_floor: string;
  to_floor: string;
  /** Floors crossed (0 for a corridor/bridge); absent from an older backend. */
  floors?: number;
  direction?: "up" | "down" | null;
  /** Walking-equivalent meters / seconds of the ride. */
  meters?: number;
  seconds?: number;
}

/** One stair or lift ride on a route; its meters are counted in `total_distance_meters`. */
export interface NavigateFloorChange {
  type: string; // "stairs" | "elevator"
  name: string;
  from_floor: string;
  to_floor: string;
  floors: number;
  /** Null when a floor level is unknown. */
  direction: "up" | "down" | null;
  meters: number;
  seconds: number;
}

export type NavigationSegment = WalkSegment | TransitionSegment;

export interface NavigateInstruction {
  type: "depart" | "walk" | "turn" | "arrive" | "transition";
  text: string;
  distance_meters: number | null;
  floorplan_id?: number;
  focus?: [number, number];
  path_portion?: [number, number][];
}

export interface NavigateResponse {
  /** Walking plus every stair/lift ride at its walking equivalent. */
  total_distance_meters: number;
  /** Flat walking only (absent from an older backend). */
  walking_meters?: number;
  floor_changes?: NavigateFloorChange[];
  /** Walking at 1.4 m/s plus the rides (absent from an older backend). */
  estimated_seconds?: number;
  segments: NavigationSegment[];
  instructions: NavigateInstruction[];
}

/** `POST /tenant-portal/navigate/nearest-exit/` `kind`: the nearest regular exit (`any`, never an
 *  emergency-only door), or an emergency route to the nearest exit of any kind (never by lift). */
export type NearestExitKind = "any" | "emergency";

export interface NearestExitInfo {
  kind: "emergency" | "exit";
  name: string;
  /** "Emergency exit: <name>" / "Exit: <name>" — also the route's end label. */
  label: string;
  door_id: number | null;
  floorplan_id: number;
  floorplan_name: string;
}

/** The `/navigate/` route, plus which exit it ends at. */
export interface NearestExitResponse extends NavigateResponse {
  exit: NearestExitInfo;
}

export interface MyRoom {
  room_id: number | null;
  /** #428: every room the member's company rents today, the main one first. */
  room_ids?: number[];
  room_name?: string;
  floor_name?: string | null;
  floorplan_id?: number | null;
  centroid?: [number, number] | null;
}

/** One floor's alarm (`GET /tenant-portal/floor-alarms/` and the `floor_alarm_changed` push).
 *  `state` null = the floor no longer has an alarm linked. */
export interface FloorAlarmRow {
  floor_id: number;
  floor_name: string;
  address_name: string;
  state: "armed" | "pre_alarm" | "off" | "unknown" | null;
  /** Start of the armed / pre-alarm episode. */
  since: string | null;
  /** Pre-alarm: minutes until the alarm arms ("in about N minutes"), set per
   *  pre-alarm source; null when not set (the banner says "soon"). */
  warning_minutes: number | null;
  /** Per-floor push order (absent on the REST read). */
  seq?: number | null;
}

/** One location drawn on a floor plan with its own alarm links (`alarm_regions`
 *  of `GET /tenant-portal/floorplans/{id}/`): a floor, an address or a room. */
export interface FloorplanAlarmRegionRow {
  location_type: "floor" | "address" | "room";
  location_id: number;
  state: "armed" | "pre_alarm" | "off" | "unknown";
  since?: string | null;
  warning_minutes?: number | null;
}

export const floorAlarmsApi = {
  list: () => api.get<FloorAlarmRow[]>("/tenant-portal/floor-alarms/"),
};

export const floorplansApi = {
  list: () => api.get("/tenant-portal/floorplans/"),
  get: (id: number) => api.get(`/tenant-portal/floorplans/${id}/`),
  getMyRoom: () => api.get<MyRoom>("/tenant-portal/my-room/"),
  navigate: (toRoomId: number, fromRoomId?: number) =>
    api.post<NavigateResponse>("/tenant-portal/navigate/", {
      to_room_id: toRoomId,
      from_room_id: fromRoomId,
    }),
  /** Walking route to the nearest exit (#473); without `fromRoomId` it starts at the member's own room. */
  nearestExit: (kind: NearestExitKind, fromRoomId?: number) =>
    api.post<NearestExitResponse>("/tenant-portal/navigate/nearest-exit/", {
      kind,
      from_room_id: fromRoomId,
    }),
};

export const meetingRoomsApi = {
  getRooms: () => api.get<MeetingRoom[]>("/tenant-portal/meeting-rooms/"),
  getSchedule: (date?: string) =>
    api.get<MeetingRoomSchedule[]>("/tenant-portal/meeting-rooms/schedule/", {
      params: date ? { date } : {},
    }),
  getRoomDetail: (roomId: number, date?: string) =>
    api.get<MeetingRoomDetail>(`/tenant-portal/meeting-rooms/${roomId}/schedule/`, {
      params: date ? { date } : {},
    }),
  bookRoom: (roomId: number, data: BookRoomRequest) =>
    api.post<BookRoomResponse>(`/tenant-portal/meeting-rooms/${roomId}/book/`, data),
  findFreeRooms: (durationMinutes: number, originRoomId?: number, limit = 5) =>
    api.get<FreeRoomCandidate[]>("/tenant-portal/meeting-rooms/free/", {
      params: {
        duration_minutes: durationMinutes,
        limit,
        ...(originRoomId !== undefined ? { origin_room_id: originRoomId } : {}),
      },
    }),
  wayfind: (roomId: number, fromRoomId?: number) =>
    api.get<WayfindResponse>(`/crm/floorplan/wayfind/`, {
      params: {
        to_room_id: roomId,
        ...(fromRoomId !== undefined ? { from_room_id: fromRoomId } : {}),
      },
    }),
  getMyBookings: () => api.get<MyBooking[]>("/tenant-portal/my-bookings/"),
  /** #401: ``withdraw`` = the member is withdrawing an approval request; 409 if an admin approved it meanwhile. */
  cancelBooking: (eventId: number, opts: { withdraw?: boolean } = {}) =>
    api.delete(`/tenant-portal/my-bookings/${eventId}/`, { params: opts.withdraw ? { withdraw: true } : undefined }),
};

export interface TenantCalendarToken {
  id: number;
  name: string;
  /** Who created the feed (name, else email). */
  created_by?: string | null;
  /** The caller may revoke it: their own feed, or any feed for a tenant admin. */
  can_manage?: boolean;
  /** Tenant admins only: may see who fetched the feed (IP, access log). */
  can_view_accesses?: boolean;
  created_at: string;
  expires_at: string | null;
  revoked_at: string | null;
  last_used_at: string | null;
  last_used_remote_addr: string | null;
  is_active: boolean;
  contents: FeedContents;
  rooms: { id: number; name: string }[];
}

export interface IssuedTenantCalendarFeed {
  token: TenantCalendarToken;
  raw_token: string;
  subscribe_url: string;
}

export interface IssueTenantCalendarFeedsResponse {
  feeds: IssuedTenantCalendarFeed[];
  /**
   * Deprecated: mirrors feeds[0], set only when exactly one feed was
   * issued (the shape every caller saw before ``feeds`` existed). Kept so
   * a stale tab survives a rolling deploy either way — see calendar-feeds-card.tsx.
   */
  token?: TenantCalendarToken;
  raw_token?: string;
  subscribe_url?: string;
}

export interface TenantCalendarTokenAccess {
  id: number;
  remote_addr: string;
  user_agent: string;
  asn_org: string;
  country: string;
  count: number;
  first_seen: string;
  last_seen: string;
}

export interface EmergencyState {
  active: boolean;
  id?: number;
  /** e.g. "emergency.evacuate", "announcement.maintenance" */
  category?: string;
  /** "critical" | "warning" | "info" */
  severity?: string;
  title?: string;
  raised_at?: string;
  message?: string;
  is_site_wide?: boolean;
}

export const emergencyApi = {
  /** Polled by the member-portal banner. Returns {active: false} when
   *  no emergency is in effect on the user's site. */
  get: () => api.get<EmergencyState>("/tenant-portal/emergency/"),
};

import type { AnnouncementWithAudio } from "@/lib/announcement-audio";

/** Where a scanned QR short link (`/q/<uid>`) takes this member. */
export interface QrResolveResponse {
  /** A portal-relative path; `/` when the object is unknown, hidden from the
   *  member or has no portal page yet. */
  portal_path: string;
  found: boolean;
  /** A soft note for the member when the code didn't lead to its page. */
  message: string | null;
}

export const qrApi = {
  resolve: (uid: string) =>
    api.get<QrResolveResponse>(`/qr/resolve/${encodeURIComponent(uid)}/`),
};

export const announcementsApi = {
  /** All active announcements for the user's site, each with its
   *  display_type (banner / corner / fullscreen) — on this feed that is the
   *  announcement's member-portal display (``portal_display_type``), set
   *  per surface in the admin form — and, when it has one, its
   *  audio clip + play schedule. Replaces the single highest-severity
   *  {@link emergencyApi.get} for the rich portal UI. */
  list: () =>
    api.get<{ announcements: AnnouncementWithAudio[] }>("/tenant-portal/announcements/"),
};

export interface FreeRoomCandidate {
  id: number;
  name: string;
  real_name?: string;
  description?: string | null;
  floor_name: string;
  address_name: string;
  seats?: number | null;
  has_camera?: boolean;
  has_tv?: boolean;
  has_speaker?: boolean;
  next_free_slot: { start: string; duration_minutes: number };
  free_for_minutes: number;
  is_same_floor: boolean;
  distance_meters?: number | null;
  same_floorplan?: boolean;
  floorplan_id?: number | null;
  pricing?: RoomPricing | null;
}

export interface WayfindResponse {
  same_floorplan: boolean;
  same_floor: boolean;
  floorplan: {
    id: number;
    name: string;
    viewbox: string;
    scale: number;
    svg_content: string;
    navigation_paths: Array<Array<[number, number]>>;
    navigation_nodes: Array<{ id?: string; x: number; y: number; name?: string; type?: string }>;
  } | null;
  origin: {
    room_id: number;
    room_name: string;
    floor_name: string;
    floor_id: number | null;
    centroid: [number, number] | null;
    geometry?: Record<string, unknown> | null;
  } | null;
  destination: {
    room_id: number;
    room_name: string;
    floor_name: string;
    floor_id: number | null;
    centroid: [number, number] | null;
    geometry?: Record<string, unknown> | null;
  };
}
